import './HeaderCMSLanguageToggle.css';
import SegmentedToggle from '../../SegmentedToggle/SegmentedToggle';
import { useState } from 'react';
import { MenuSection } from '../../Modals/SideMenu/components/MenuSection';
import { MenuItem } from '../../Modals/SideMenu/components/MenuItem';
import { translate, getCMSUrlPrefixByLocale } from '../../../utils/translator';

interface LanguageToggleProps {
  country: string;
  language: string;
  isDesktopDesign?: boolean;
  wrapperClassName?: string;
  titleClassName?: string;
  itemClassName?: string;
  isAbsolute?: boolean;
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({
  country, 
  language, 
  isDesktopDesign=true,
  wrapperClassName,
  titleClassName,
  itemClassName,
}) => {
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

  // Set the language in the events tracking
  // Language is passed from the parent component as well:
  // for CMS header - it is passed from the context - from events tracking (set the same value in events tracking)
  // for Funnel header - it is passed from the context - from URL
  window.eventsTracking.setData('language', language);

  const options = [
    { value: "de", label: "Deutsch" },
    { value: "fr", label: "Français" },
  ];

  const chDePrefix = getCMSUrlPrefixByLocale('de-CH');
  const chFrPrefix = getCMSUrlPrefixByLocale('fr-CH');
  const countryLower = country.toLowerCase();

  // Only show toggle for CH countries (ch, ch-de, ch-fr)
  if (countryLower !== 'ch' && countryLower !== chDePrefix && countryLower !== chFrPrefix) {
    return null;
  }

  const handleLanguageToggle = (selectedLanguage: string) => {
    if (selectedLanguage === language) return;
    // Track the language change
    window.eventsTracking.setData('language', selectedLanguage);
    
    const currentPath = window.location.pathname;
    const pathSegments = currentPath.split('/').filter(Boolean);
    const queryString = window.location.search;

    const firstSegment = pathSegments[0]?.toLowerCase();

    // Auth/CMS routes: /ch-de/login, /ch-fr/register, etc.
    if (firstSegment === chDePrefix || firstSegment === chFrPrefix) {
      pathSegments[0] = getCMSUrlPrefixByLocale(`${selectedLanguage}-CH`);
      window.location.href = '/' + pathSegments.join('/') + queryString;
      return;
    }

    // Legacy /ch/... routes: no URL change
    if (firstSegment === 'ch') {
      window.location.href = '/' + pathSegments.join('/') + queryString;
      return;
    }

    // Funnel routes: /select_treatment/ch/fr/erect, /shipping/ch/de/..., etc.
    const languageIndex = pathSegments.findIndex(segment => segment?.toLowerCase() === language?.toLowerCase());
    if (languageIndex !== -1) {
      pathSegments[languageIndex] = selectedLanguage;
    }
    
    const newPath = '/' + pathSegments.join('/');
    window.location.href = newPath + queryString;
  };

  
  const openLanguageModal = () => {
    setIsLanguageModalOpen(true);
  };

  const closeLanguageModal = () => {
    setIsLanguageModalOpen(false);
  };

  return (
    <>
      {isDesktopDesign ? (
        <li className={`nav-language-toggle`}>
          <SegmentedToggle
            options={options}
            selected={language}
            onChange={handleLanguageToggle}
            className="language-toggle-segmented "
          />
        </li>
      ) : wrapperClassName || titleClassName || itemClassName ? (
        // Custom styling for InfoSection integration
        <div className={wrapperClassName || ''}>
          {titleClassName && (
            <span className={titleClassName}>
              {translate(language, "my_account", "SideMenu/settings")}
            </span>
          )}
          <div
            className={itemClassName || 'flex items-center justify-between w-full h-[3.375rem] bg-transparent cursor-pointer hover:bg-[#f2f4f6] rounded-[0.75rem]'}
            style={{ transition: 'background-color 0.2s ease' }}
            onClick={openLanguageModal}
          >
            <span className="font-poppins font-medium text-[#1f2937] leading-[21px] text-[16px] tracking-normal">
              {options.find(option => option.value === language)?.label}
            </span>
            <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="h-[9.33px] w-[5.33px] shrink-0" style={{ color: '#98a0af' }} />
          </div>
        </div>
      ) : (
        // Default MenuSection styling
        <div className="side-menu-section">
          <MenuSection
            id="toggle-language-section"
            title={translate(language, "my_account", "SideMenu/settings")}
            isMobileTitle={true}
          >
            <MenuItem
              key="language-toggle"
              label={options.find(option => option.value === language)?.label || ''}
              showChevron={true}
              onClick={openLanguageModal}
              className="side-menu-mobile-item"
            />
          </MenuSection>
        </div>
      )}

      {/* Language Selection Modal */}
      {isLanguageModalOpen && (
        <div className="language-modal-overlay" onClick={closeLanguageModal}>
          <div className="language-modal" onClick={event => event.stopPropagation()}>
            <div className="language-modal-content">
              <h3 className="language-modal-title">
                {translate(language, "my_account", "SideMenu/select_language")}
              </h3>
              <div className="language-options">
                {options.map(languageOption => (
                  <div 
                    key={languageOption.value}
                    className="language-option" 
                    onClick={() => handleLanguageToggle(languageOption.value)}
                  >
                    <div className={`language-radio ${languageOption.value === language ? 'checked' : ''}`}>
                      {languageOption.value === language && <div className="language-radio-dot"></div>}
                    </div>
                    <span className="language-option-text">{languageOption.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LanguageToggle; 